import React, { useState } from "react";
import { Button, IconButton, Dialog, DialogTitle, DialogContent, DialogActions, TextField, Snackbar, Alert, FormControlLabel, Switch, Typography } from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import AddIcon from "@mui/icons-material/Add";
import AdminPanelSettingsIcon from "@mui/icons-material/AdminPanelSettings";
import { useNavigate } from "react-router-dom";
import { API, graphqlOperation } from "aws-amplify";
import { createAlert, updateAlert } from "../graphql/mutations";
import { useUser } from "../UserContext";
import { useNotifications } from "../NotificationContext";

const Notifications = () => {
  const navigate = useNavigate();
  const { userRecord, userClass, activeProvider } = useUser();
  const { notifications, loading, refreshNotifications, markAsRead } = useNotifications();
  const [openDialog, setOpenDialog] = useState(false);
  const [newNotification, setNewNotification] = useState({
    title: "",
    content: "",
    isAdminNotification: false
  });
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success"
  });

  const isAdmin = userClass === "Admin" || userClass === "SuperAdmin";
  const isSuperAdmin = userClass === "SuperAdmin";

  const handleOpenDialog = () => {
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setNewNotification({ 
      title: "", 
      content: "", 
      isAdminNotification: false 
    });
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setNewNotification(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSwitchChange = (e) => {
    setNewNotification(prev => ({
      ...prev,
      isAdminNotification: e.target.checked
    }));
  };

  const handleCreateNotification = async () => {
    try {
      if (!newNotification.title || !newNotification.content) {
        setSnackbar({
          open: true,
          message: "Please fill in all fields",
          severity: "error"
        });
        return;
      }

      // Only SuperAdmins can create admin notifications
      const isAdminNotification = isSuperAdmin ? newNotification.isAdminNotification : false;

      const input = {
        title: newNotification.title,
        content: newNotification.content,
        userID: userRecord.id,
        read: false,
        createdAt: new Date().toISOString(),
        isAdminNotification
      };

      await API.graphql(graphqlOperation(createAlert, { input }));
      
      handleCloseDialog();
      setSnackbar({
        open: true,
        message: "Notification created successfully!",
        severity: "success"
      });
      
      // Refresh notifications list
      refreshNotifications();
    } catch (error) {
      console.error("Error creating notification:", error);
      setSnackbar({
        open: true,
        message: "Error creating notification",
        severity: "error"
      });
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  const handleBack = () => {
    navigate(-1);
  };

  const handleViewNotification = (notificationId) => {
    // Mark notification as read
    markAsRead(notificationId);
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffTime = Math.abs(now - date);
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) {
      return "Today";
    } else if (diffDays === 1) {
      return "Yesterday";
    } else if (diffDays < 7) {
      return `${diffDays} days ago`;
    } else {
      return date.toLocaleDateString('en-US', { 
        year: 'numeric', 
        month: 'long', 
        day: 'numeric' 
      });
    }
  };

  // Get the sender name for a notification
  const getSenderName = (notification) => {
    if (notification.isAdminNotification) {
      return "System Admin";
    }
    
    if (notification.user && notification.user.companyName) {
      return notification.user.companyName;
    }
    
    if (activeProvider && notification.userID === activeProvider.id) {
      return activeProvider.companyName || "Your Provider";
    }
    
    return "Unknown Sender";
  };

  return (
    <div style={{ maxWidth: "800px", margin: "30px auto", padding: "20px" }}>
      {/* Header */}
      <div style={{ 
        display: "flex", 
        alignItems: "center", 
        marginBottom: "40px",
        position: "relative" 
      }}>
        <Button
          startIcon={<ArrowBackIcon />}
          style={{
            backgroundColor: "#4ADE80",
            color: "black",
            borderRadius: "20px",
            position: "absolute",
            left: 0,
          }}
          onClick={handleBack}
        >
          Back
        </Button>
        <h1 style={{ 
          margin: "0 auto",
          textTransform: "uppercase",
          fontWeight: "bold"
        }}>
          NOTIFICATIONS
        </h1>
        
        {isAdmin && (
          <Button
            startIcon={<AddIcon />}
            style={{
              backgroundColor: "#333",
              color: "white",
              borderRadius: "20px",
              position: "absolute",
              right: 0,
            }}
            onClick={handleOpenDialog}
          >
            Create
          </Button>
        )}
      </div>

      {/* Notifications List */}
      <div style={{ display: "flex", flexDirection: "column", gap: "20px" }}>
        {loading ? (
          <p>Loading notifications...</p>
        ) : notifications.length === 0 ? (
          <p>No notifications available</p>
        ) : (
          notifications.map((notification) => (
            <div
              key={notification.id}
              style={{
                backgroundColor: notification.isAdminNotification ? "#FEF3C7" : (notification.read ? "#4ADE80" : "#5EEAD4"),
                borderRadius: "15px",
                padding: "20px",
                position: "relative",
                borderLeft: notification.read ? "none" : (notification.isAdminNotification ? "5px solid #F59E0B" : "5px solid #0891B2")
              }}
            >
              {notification.isAdminNotification && (
                <div style={{ 
                  position: "absolute", 
                  top: "10px", 
                  right: "10px",
                  display: "flex",
                  alignItems: "center",
                  backgroundColor: "#F59E0B",
                  color: "white",
                  padding: "4px 8px",
                  borderRadius: "12px",
                  fontSize: "0.75rem"
                }}>
                  <AdminPanelSettingsIcon style={{ fontSize: "1rem", marginRight: "4px" }} />
                  SYSTEM
                </div>
              )}
              
              <div style={{ marginBottom: "20px" }}>
                <h2 style={{ 
                  fontSize: "1.5rem", 
                  marginBottom: "10px",
                  color: "black"
                }}>
                  {notification.title}
                </h2>
                <p style={{ 
                  color: "black",
                  marginBottom: "10px" 
                }}>
                  {notification.content}
                </p>
                <a 
                  href="#" 
                  style={{ 
                    color: "black",
                    textDecoration: "underline" 
                  }}
                >
                  Read More
                </a>
              </div>
              
              <div style={{ 
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                borderTop: "1px solid rgba(0,0,0,0.1)",
                paddingTop: "10px"
              }}>
                <div>
                  <span style={{ 
                    color: "black",
                    display: "block"
                  }}>
                    Posted {formatDate(notification.createdAt)}
                  </span>
                  <span style={{ 
                    color: "black",
                    fontSize: "0.85rem",
                    opacity: 0.8
                  }}>
                    From: {getSenderName(notification)}
                  </span>
                </div>
                <Button
                  endIcon={<OpenInNewIcon />}
                  style={{
                    backgroundColor: notification.isAdminNotification ? "#F59E0B" : "#333",
                    color: "white",
                    borderRadius: "20px",
                    padding: "5px 15px",
                  }}
                  onClick={() => handleViewNotification(notification.id)}
                >
                  View
                </Button>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Create Notification Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          Create New Notification
          {isSuperAdmin && (
            <Typography variant="subtitle2" color="text.secondary">
              As a SuperAdmin, you can send notifications to all users
            </Typography>
          )}
          {!isSuperAdmin && isAdmin && (
            <Typography variant="subtitle2" color="text.secondary">
              Your notification will be sent to your customers only
            </Typography>
          )}
        </DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            name="title"
            label="Title"
            type="text"
            fullWidth
            variant="outlined"
            value={newNotification.title}
            onChange={handleInputChange}
            sx={{ mb: 2 }}
          />
          <TextField
            margin="dense"
            name="content"
            label="Content"
            type="text"
            fullWidth
            variant="outlined"
            multiline
            rows={4}
            value={newNotification.content}
            onChange={handleInputChange}
            sx={{ mb: 2 }}
          />
          
          {isSuperAdmin && (
            <FormControlLabel
              control={
                <Switch
                  checked={newNotification.isAdminNotification}
                  onChange={handleSwitchChange}
                  color="warning"
                />
              }
              label="Send as System Notification (to all users)"
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button 
            onClick={handleCreateNotification} 
            variant="contained" 
            style={{ 
              backgroundColor: newNotification.isAdminNotification ? "#F59E0B" : "#4ADE80", 
              color: newNotification.isAdminNotification ? "white" : "black" 
            }}
          >
            Create
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar 
        open={snackbar.open} 
        autoHideDuration={6000} 
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </div>
  );
};

export default Notifications;
